import React, { useMemo, useState } from 'react';
import { 
  ComposableMap, 
  Geographies, 
  Geography, 
  ZoomableGroup 
} from 'react-simple-maps';
import { Tooltip as AntTooltip, Button, Space } from 'antd';
import { PlusOutlined, MinusOutlined, AimOutlined } from '@ant-design/icons';
import { useTheme } from '../contexts/ThemeContext';

// World map topojson URL
const geoUrl = "https://unpkg.com/world-atlas@2.0.2/countries-110m.json";

// Regional color mapping (should match RegionalAnalysis.tsx)
const REGION_COLORS: { [key: string]: string } = {
  'North America': '#1890ff',      // Blue
  'Europe': '#52c41a',             // Green
  'Asia Pacific': '#faad14',       // Orange
  'Middle East & Africa': '#f5222d', // Red
  'Latin & Central America': '#722ed1', // Purple
  'Eastern Europe': '#13c2c2',     // Cyan
  'Unknown': '#8c8c8c'             // Gray (instead of orange to avoid confusion)
};

// Mapping of country names in topojson to dashboard regions
const countryToRegion: Record<string, string> = {
  // North America
  "United States of America": "North America",
  "USA": "North America",
  "Canada": "North America",
  "Greenland": "North America",
  "Mexico": "Latin & Central America", 

  // Europe
  "France": "Europe",
  "Germany": "Europe",
  "Greece": "Europe",
  "Italy": "Europe",
  "Netherlands": "Europe",
  "Poland": "Europe",
  "Portugal": "Europe",
  "Russia": "Europe",
  "Spain": "Europe",
  "Switzerland": "Europe",
  "Turkey": "Europe",
  "United Kingdom": "Europe",
  "Ireland": "Europe",
  "Belgium": "Europe",
  "Austria": "Europe",
  "Sweden": "Europe",
  "Norway": "Europe",
  "Finland": "Europe",
  "Denmark": "Europe",
  "Ukraine": "Europe",
  "Belarus": "Europe",
  "Czechia": "Europe",
  "Slovakia": "Europe",
  "Hungary": "Europe",
  "Romania": "Europe",
  "Bulgaria": "Europe",
  "Croatia": "Europe",
  "Slovenia": "Europe",
  "Bosnia and Herz.": "Europe",
  "Albania": "Europe",
  "Moldova": "Europe",
  "Estonia": "Europe",
  "Latvia": "Europe",
  "Lithuania": "Europe",
  "Iceland": "Europe",
  "Cyprus": "Europe",
  "Vatican City": "Europe",
  "San Marino": "Europe",
  "Andorra": "Europe",
  "Monaco": "Europe",
  "Liechtenstein": "Europe",
  "Luxembourg": "Europe",
  "North Macedonia": "Europe",
  "Macedonia": "Europe",
  "Kosovo": "Europe",
  "Serbia": "Europe",
  "Montenegro": "Europe",

  // Asia Pacific
  "Australia": "Asia Pacific",
  "China": "Asia Pacific",
  "India": "Asia Pacific",
  "Japan": "Asia Pacific",
  "South Korea": "Asia Pacific",
  "North Korea": "Asia Pacific",
  "Taiwan": "Asia Pacific",
  "New Zealand": "Asia Pacific",
  "Vietnam": "Asia Pacific",
  "Thailand": "Asia Pacific",
  "Indonesia": "Asia Pacific",
  "Malaysia": "Asia Pacific",
  "Philippines": "Asia Pacific",
  "Myanmar": "Asia Pacific",
  "Cambodia": "Asia Pacific",
  "Laos": "Asia Pacific",
  "Bangladesh": "Asia Pacific",
  "Pakistan": "Asia Pacific",
  "Sri Lanka": "Asia Pacific",
  "Nepal": "Asia Pacific",
  "Bhutan": "Asia Pacific",
  "Mongolia": "Asia Pacific",
  "Papua New Guinea": "Asia Pacific",
  "Kazakhstan": "Asia Pacific",
  "Uzbekistan": "Asia Pacific",
  "Turkmenistan": "Asia Pacific",
  "Kyrgyzstan": "Asia Pacific",
  "Tajikistan": "Asia Pacific",
  "Afghanistan": "Asia Pacific",
  "Armenia": "Asia Pacific",
  "Azerbaijan": "Asia Pacific",
  "Georgia": "Asia Pacific",

  // Middle East & Africa (Extensive list to avoid "Unknown" yellow spots)
  "Egypt": "Middle East & Africa",
  "Israel": "Middle East & Africa",
  "Saudi Arabia": "Middle East & Africa",
  "South Africa": "Middle East & Africa",
  "Nigeria": "Middle East & Africa",
  "Kenya": "Middle East & Africa",
  "Algeria": "Middle East & Africa",
  "Libya": "Middle East & Africa",
  "Morocco": "Middle East & Africa",
  "Tunisia": "Middle East & Africa",
  "Sudan": "Middle East & Africa",
  "South Sudan": "Middle East & Africa",
  "S. Sudan": "Middle East & Africa",
  "Ethiopia": "Middle East & Africa",
  "Somalia": "Middle East & Africa",
  "Somaliland": "Middle East & Africa",
  "Tanzania": "Middle East & Africa",
  "Uganda": "Middle East & Africa",
  "DR Congo": "Middle East & Africa",
  "Dem. Rep. Congo": "Middle East & Africa",
  "Congo": "Middle East & Africa",
  "Angola": "Middle East & Africa",
  "Namibia": "Middle East & Africa",
  "Botswana": "Middle East & Africa",
  "Zimbabwe": "Middle East & Africa",
  "Mozambique": "Middle East & Africa",
  "Zambia": "Middle East & Africa",
  "Malawi": "Middle East & Africa",
  "Madagascar": "Middle East & Africa",
  "Chad": "Middle East & Africa",
  "Niger": "Middle East & Africa",
  "Mali": "Middle East & Africa",
  "Mauritania": "Middle East & Africa",
  "Western Sahara": "Middle East & Africa",
  "W. Sahara": "Middle East & Africa",
  "Senegal": "Middle East & Africa",
  "Gambia": "Middle East & Africa",
  "Guinea": "Middle East & Africa",
  "Guinea-Bissau": "Middle East & Africa",
  "Sierra Leone": "Middle East & Africa",
  "Liberia": "Middle East & Africa",
  "Ivory Coast": "Middle East & Africa",
  "Côte d'Ivoire": "Middle East & Africa",
  "Ghana": "Middle East & Africa",
  "Togo": "Middle East & Africa",
  "Benin": "Middle East & Africa",
  "Burkina Faso": "Middle East & Africa",
  "Cameroon": "Middle East & Africa",
  "Central African Rep.": "Middle East & Africa",
  "Central African Republic": "Middle East & Africa",
  "Gabon": "Middle East & Africa",
  "Eq. Guinea": "Middle East & Africa",
  "Equatorial Guinea": "Middle East & Africa",
  "Eritrea": "Middle East & Africa",
  "Djibouti": "Middle East & Africa",
  "Jordan": "Middle East & Africa",
  "Syria": "Middle East & Africa",
  "Iraq": "Middle East & Africa",
  "Iran": "Middle East & Africa",
  "Oman": "Middle East & Africa",
  "Yemen": "Middle East & Africa",
  "United Arab Emirates": "Middle East & Africa",
  "Qatar": "Middle East & Africa",
  "Kuwait": "Middle East & Africa",
  "Lebanon": "Middle East & Africa",
  "Palestine": "Middle East & Africa",
  "Burundi": "Middle East & Africa",
  "Rwanda": "Middle East & Africa",
  "Eswatini": "Middle East & Africa",
  "eSwatini": "Middle East & Africa",
  "Swaziland": "Middle East & Africa",
  "Lesotho": "Middle East & Africa",

  // Latin & Central America
  "Brazil": "Latin & Central America",
  "Argentina": "Latin & Central America",
  "Chile": "Latin & Central America",
  "Colombia": "Latin & Central America",
  "Peru": "Latin & Central America",
  "Venezuela": "Latin & Central America",
  "Ecuador": "Latin & Central America",
  "Bolivia": "Latin & Central America",
  "Paraguay": "Latin & Central America",
  "Uruguay": "Latin & Central America",
  "Guyana": "Latin & Central America",
  "Suriname": "Latin & Central America",
  "French Guiana": "Latin & Central America",
  "Fr. Guiana": "Latin & Central America",
  "French Guiana (France)": "Latin & Central America",
  "Guyane": "Latin & Central America",
  "Guyane française": "Latin & Central America",
  "Panama": "Latin & Central America",
  "Costa Rica": "Latin & Central America",
  "Nicaragua": "Latin & Central America",
  "Honduras": "Latin & Central America",
  "El Salvador": "Latin & Central America",
  "Guatemala": "Latin & Central America",
  "Belize": "Latin & Central America",
  "Cuba": "Latin & Central America",
  "Haiti": "Latin & Central America",
  "Dominican Rep.": "Latin & Central America",
  "Jamaica": "Latin & Central America",
  "Puerto Rico": "Latin & Central America",
  "Trinidad and Tobago": "Latin & Central America",
  "Bahamas": "Latin & Central America"
};

interface WorldMapProps {
  selectedRegions: string[];
  regionalData?: any[];
  compact?: boolean;
}

const formatRevenue = (value: number) => {
  if (value === 0) return '$0.0M';
  if (Math.abs(value) < 1.0) {
    return `$${value.toFixed(2)}M`;
  }
  return `$${value.toFixed(1)}M`;
};

const WorldMap: React.FC<WorldMapProps> = ({ selectedRegions, regionalData = [], compact = false }) => {
  const { isDark } = useTheme();
  const [position, setPosition] = useState({ coordinates: [10, -12], zoom: 0.83 });
  const [isDragging, setIsDragging] = useState(false);

  const handleZoomIn = () => {
    if (position.zoom >= 6) return;
    setPosition((pos) => ({ ...pos, zoom: pos.zoom * 1.2 }));
  };

  const handleZoomOut = () => {
    if (position.zoom <= 0.8) return;
    setPosition((pos) => ({ ...pos, zoom: pos.zoom / 1.2 }));
  };

  const handleReset = () => {
    setPosition({ coordinates: [10, -12], zoom: 0.83 });
  };

  const handleMoveEnd = (position: any) => {
    setPosition(position);
    setIsDragging(false);
  };

  const getFillColor = (region: string) => {
    if (selectedRegions.length > 0 && !selectedRegions.includes(region)) {
      return isDark ? "#2a2a2a" : "#e5e7eb";
    }
    return REGION_COLORS[region] || (isDark ? "#333" : "#f0f0f0");
  };

  const mapStyles = useMemo(() => ({
    width: "100%",
    height: "100%",
    cursor: isDragging ? "grabbing" : "grab",
  }), [isDragging]);

  return (
    <div
      style={{ 
        width: '100%', 
        height: 'calc(100% - 20px)',
        minHeight: '300px',
        backgroundColor: isDark ? '#141414' : '#f9fafb', 
        borderRadius: '8px', 
        overflow: 'hidden',
        position: 'relative',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        borderBottom: `2px solid ${isDark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)'}`,
        marginBottom: '20px',
      }}
      onMouseDown={() => setIsDragging(true)}
      onMouseUp={() => setIsDragging(false)}
      onMouseLeave={() => setIsDragging(false)}
    >
      {/* Zoom Controls */}
      <div style={{
        position: 'absolute',
        right: '16px',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }}>
        <Space direction="vertical">
          <Button 
            icon={<PlusOutlined />} 
            onClick={handleZoomIn} 
            disabled={position.zoom >= 6}
            title="Zoom In"
          />
          <Button 
            icon={<MinusOutlined />} 
            onClick={handleZoomOut} 
            disabled={position.zoom <= 0.8}
            title="Zoom Out"
          />
          <Button 
            icon={<AimOutlined />} 
            onClick={handleReset}
            title="Reset View"
          />
        </Space>
      </div>

      <ComposableMap
        projectionConfig={{
          scale: compact ? 210 : 235,
          center: [10, 0],
        }}
        style={mapStyles}
      >
        <ZoomableGroup
          zoom={position.zoom}
          center={position.coordinates as [number, number]}
          onMoveStart={() => setIsDragging(true)}
          onMoveEnd={handleMoveEnd}
          minZoom={0.5}
          maxZoom={10}
        >
          {/* Invisible background rect to capture drag events on empty areas */}
          <rect
            x={-500}
            y={-500}
            width={2000}
            height={1500}
            fill="transparent"
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          />
          <Geographies geography={geoUrl}>
            {({ geographies }: { geographies: any[] }) =>
              geographies
                .filter(geo => geo.properties.name !== "Antarctica") // Remove Antarctica
                .map((geo: any) => {
                  const countryName = geo.properties.name;
                  const region = countryToRegion[countryName] || "Unknown";
                  const isSelected = selectedRegions.length === 0 || selectedRegions.includes(region);
                  const regionStats = regionalData.find(item => item.name === region);
                  
                  const tooltipContent = (
                    <div style={{ padding: '2px' }}>
                      {region !== 'Unknown' ? (
                        <div style={{ paddingBottom: '2px' }}>
                          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', fontWeight: 600 }}>{region}</div>
                          {regionStats ? (
                            <div style={{ marginTop: '4px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '4px' }}>
                              <div style={{ fontSize: '13px' }}>
                                Revenue: <span style={{ fontWeight: 700, color: '#fff' }}>{formatRevenue(regionStats.revEnd)}</span>
                              </div>
                              <div style={{ fontSize: '13px' }}>
                                Market Share: <span style={{ fontWeight: 700, color: '#fff' }}>{regionStats.shareEnd.toFixed(1)}%</span>
                              </div>
                            </div>
                          ) : (
                            <div style={{ fontSize: '12px', fontStyle: 'italic', color: 'rgba(255,255,255,0.6)', marginTop: '4px' }}>
                              No regional data available
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontWeight: 600, fontSize: '13px' }}>{countryName}</div>
                      )}
                    </div>
                  );
                  
                  return (
                    <AntTooltip
                      key={geo.rsmKey}
                      title={tooltipContent}
                      mouseEnterDelay={0.05}
                      overlayStyle={{ pointerEvents: 'none' }}
                    >
                      <Geography
                        geography={geo}
                        fill={getFillColor(region)}
                        stroke={isDark ? "#141414" : "#ffffff"}
                        strokeWidth={0.5}
                        style={{
                          default: { outline: "none" },
                          hover: { 
                            fill: isSelected ? (REGION_COLORS[region] ? `${REGION_COLORS[region]}cc` : (isDark ? "#444" : "#ccc")) : (isDark ? "#333" : "#ddd"),
                            outline: "none",
                            cursor: "pointer",
                            transition: "fill 0.2s ease"
                          },
                          pressed: { outline: "none" },
                        }}
                      />
                    </AntTooltip>
                  );
                })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>
    </div>
  );
};

export default WorldMap;
